"use client";

import { Card } from "@inflow/ui/components/card";
import {
  Children,
  type CSSProperties,
  type PropsWithChildren,
  useEffect,
  useState,
} from "react";

const BACKGROUND_IMAGES = [
  '/images/bga.webp',
  '/images/bgb.webp',
  '/images/bgc.webp',
  '/images/bgd.webp',
  '/images/bge.webp',
  '/images/bgf.webp',
  '/images/bgg.webp',
  '/images/bgh.webp',
];

const CYCLE_INTERVAL = 18000;

export default function ({ children }: PropsWithChildren) {
  const childCount = Children.count(children);

  // Start with 0 to match server, but keep track of whether we are 'ready' to show the first image
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // 1. Create a local Record to track images (acting as a cache)
    const loadedImages: Record<string, boolean> = {};

    const switchImage = (index: number) => {
      const src = BACKGROUND_IMAGES[index];

      // OPTIMIZATION: Check the record.
      // If true, we skip the network request entirely.
      if (loadedImages[src]) {
        setActiveImageIndex(index);
        setIsReady(true);
        return;
      }

      // Otherwise, perform the preload
      const img = new Image();
      img.src = src;

      img.onload = () => {
        loadedImages[src] = true; // Mark as loaded in our Record
        setActiveImageIndex(index);
        setIsReady(true);
      };
    };

    // 2. Initial Load
    let nextIndex = Math.floor(Math.random() * BACKGROUND_IMAGES.length);
    switchImage(nextIndex);

    // 3. Set up the cycle
    const intervalId = setInterval(() => {
      let newIndex: number;
      do {
        newIndex = Math.floor(Math.random() * BACKGROUND_IMAGES.length);
      } while (newIndex === nextIndex && BACKGROUND_IMAGES.length > 1);

      nextIndex = newIndex;
      switchImage(newIndex);
    }, CYCLE_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="h-dvh p-3 pt-14 md:p-6 md:pt-16">
      <Card className="relative mx-auto grid h-full max-w-hero content-normal justify-items-center gap-3 overflow-hidden p-3 md:p-6 lg:content-center lg:p-12">

        {/* Background Image Layers */}
        <div className="absolute inset-0 z-0">
          {BACKGROUND_IMAGES.map((src, index) => (
            <div
              key={src}
              className="absolute inset-0 bg-center bg-cover transition-opacity duration-1000 ease-in-out"
              style={{
                backgroundImage: `url('${src}')`,
                // Only show the active image, and only if we are ready (preventing flash of index 0)
                opacity: isReady && activeImageIndex === index ? 1 : 0,
                // Ensure the active image is on top during transitions
                zIndex: activeImageIndex === index ? 1 : 0
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div
          style={{ "--child-count": childCount } as CSSProperties}
          className="container relative z-10 mx-auto grid grid-cols-1 grid-rows-[1fr_auto] items-center justify-items-center gap-6 lg:grid-cols-[repeat(var(--child-count),1fr)] lg:grid-rows-1"
        >
          {children}
        </div>
      </Card>
    </div>
  );
}
